import { ElementType, FC, Fragment, PropsWithChildren, useState } from 'react';
import ImageUploading, { ImageType as ImageUploadType } from 'react-images-uploading';
import { alpha, Box, BoxProps, Paper, styled } from '@mui/material';
import classNames from 'classnames';

import { useDraggableContext } from '@components/base';

import UploadImageDialog, {
  UploadImageDialogProps,
} from '../media/images/Images/UploadImageDialog';

export type InteractiveSurfaceProps = BoxProps & {
  className?: string;
  disableImageUploading?: boolean;
  disableIndicateOnDrag?: boolean;
  hideChildrenOnDragImage?: boolean;
  imageLocation?: string;
  onUploadImage?: UploadImageDialogProps['onSuccess'];
  selected?: boolean;
};

const Container = styled(Box)<BoxProps & { component: ElementType }>`
  &.dragging-image * {
    pointer-events: none;
  }

  &.dragging-image.hide-children * {
    opacity: 0;
  }

  &.dragging,
  &.dragging-image,
  &.open {
    background: ${({ theme }) => alpha(theme.palette.info.main, 0.5)} !important;
    outline-offset: -3px;
  }

  &.dragging-outline {
    outline: 3px solid ${({ theme }) => theme.palette.info.light};
  }

  &.dragging-image {
    outline: 3px dashed ${({ theme }) => theme.palette.info.light};
  }

  &.open {
    outline: 3px solid ${({ theme }) => theme.palette.info.light};
  }
`;

export const InteractiveSurface: FC<PropsWithChildren<InteractiveSurfaceProps>> = ({
  children,
  hideChildrenOnDragImage,
  disableImageUploading,
  imageLocation,
  disableIndicateOnDrag,
  selected,
  className,
  onUploadImage,
  ...rest
}) => {
  const { draggableProvided: { dragHandleProps } = {}, isDragging: isDraggingItem } =
    useDraggableContext();

  const [image, setImage] = useState<ImageUploadType>();
  const [uploadingImg, setUploadingImg] = useState(false);

  return (
    <Fragment>
      <ImageUploading onChange={imageList => setImage(imageList[0])} value={image ? [image] : []}>
        {({ isDragging, dragProps }) => (
          <Container
            className={classNames(className, {
              'dragging-image': isDragging,
              'hide-children': hideChildrenOnDragImage,
              dragging: !disableIndicateOnDrag && isDraggingItem,
              'dragging-outline': disableIndicateOnDrag && isDraggingItem,
              open: selected,
            })}
            component={Paper}
            {...(!disableImageUploading && !uploadingImg ? dragProps : {})}
            {...dragHandleProps}
            {...rest}
          >
            {disableImageUploading}
            {children}
          </Container>
        )}
      </ImageUploading>

      {image && (
        <UploadImageDialog
          image={image}
          loading={uploadingImg}
          location={imageLocation}
          onClose={() => setImage(undefined)}
          onSuccess={onUploadImage}
          setLoading={setUploadingImg}
        />
      )}
    </Fragment>
  );
};
